import { ref, update } from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import { JOB_TIMEOUT } from "../i18n/keysTranslations";
import { getPathDatabaseByRole, renewToken } from "./auth";
import axios from "../lib/axios";
import { getCloudFunctionUrl } from "../utils/cloudFunctions";
import { db } from "../lib/firebase";
import i18next from "i18next";
import { getLanguageFormatted } from "../utils/string";
import {
  CREATIVE_JOB_IN_PROGRESS_STATUSES,
  CREATIVE_JOB_STATUS,
} from "../utils/creativeJobStatus";
import { isCreativeJobResultError } from "../utils/creativeJobSentryReport";
import { LaunchError } from "../utils/errors";
import { store } from "../app/store";

const MINUTE_IN_MS = 60 * 1000;
const CREATIVE_JOB_TIMEOUT_MS = 20 * MINUTE_IN_MS;
const CREATIVE_JOB_TIMEOUTS_BY_TYPE = {
  generateImages: 9 * MINUTE_IN_MS,
  resizeImage: 9 * MINUTE_IN_MS,
  editImage: 9 * MINUTE_IN_MS,
  editImagesBatch: 18 * MINUTE_IN_MS,
  resizeImagesBatch: 20 * MINUTE_IN_MS,
  animateImage: 10 * MINUTE_IN_MS,
  generateVideo: 30 * MINUTE_IN_MS,
  generateRecommendedSettingsPlatform: 20 * MINUTE_IN_MS,
  generateCustomReport: 15 * MINUTE_IN_MS,
  analyzeImageLayers: 9 * MINUTE_IN_MS,
  eraseLayerObject: 9 * MINUTE_IN_MS,
  analyzeCreativeTemplate: 9 * MINUTE_IN_MS,
  produceCreativeTemplate: 61 * MINUTE_IN_MS,
  analyzeBulkCampaigns: 9 * MINUTE_IN_MS,
  generateBulkCampaigns: 9 * MINUTE_IN_MS,
};

const getCreativeJobError = (error = {}) => ({
  ok: false,
  ...error,
});

const getCreativeJobTimeoutMs = ({ jobType, timeoutMs }) =>
  timeoutMs ??
  CREATIVE_JOB_TIMEOUTS_BY_TYPE[jobType] ??
  CREATIVE_JOB_TIMEOUT_MS;

const getCreativeJobTimeoutError = () => ({
  ok: false,
  message: "Creative job timeout",
  code: JOB_TIMEOUT,
});

/**
 * Calls createJob HTTPS function, then waits on RTDB jobs/{userID}/{jobID}
 * until status is success, error, or cancelled (keeps waiting on pending/processing).
 *
 * Resolves only with successful job responses. Rejects when createJob fails,
 * when the job finishes with an error response, or when the job times out.
 */
export const createCreativeJob = async ({
  dispatch,
  jobType,
  payload,
  language: languageOverride,
}) => {
  const token = await dispatch(renewToken());
  const { userID } = dispatch(getPathDatabaseByRole());
  const language = languageOverride ?? getLanguageFormatted(i18next.language);

  const { data } = await axios({
    method: "post",
    url: `${getCloudFunctionUrl("createJob")}/createJob`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      jobType,
      payload,
      language,
    },
  });

  const jobID = data?.data?.jobID;
  if (!data?.ok || !jobID || !userID) {
    throw getCreativeJobError(data);
  }

  return { jobID, userID };
};

export const waitForCreativeJobResponse = async ({
  userID,
  jobID,
  timeoutMs,
}) => {
  const timeoutMsFormatted = timeoutMs ?? CREATIVE_JOB_TIMEOUT_MS;

  return await new Promise((resolve, reject) => {
    const jobRef = ref(db, `jobs/${userID}/${jobID}`);
    let isSettled = false;
    let unsubscribe = null;
    let timeoutID = null;

    const onCleanup = () => {
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
      if (timeoutID != null) {
        clearTimeout(timeoutID);
        timeoutID = null;
      }
    };

    const onResolve = (response) => {
      if (isSettled) return;
      const responseFormatted = getCreativeJobError(response);

      if (!responseFormatted?.ok) {
        onReject(responseFormatted);
        return;
      }

      isSettled = true;
      onCleanup();
      resolve({ ...responseFormatted, jobID });
    };

    const onReject = (error) => {
      if (isSettled) return;
      isSettled = true;
      onCleanup();
      reject(error);
    };

    timeoutID = setTimeout(async () => {
      const timeoutError = getCreativeJobTimeoutError();

      try {
        await update(jobRef, {
          status: CREATIVE_JOB_STATUS.CANCELLED,
          response: timeoutError,
        });
      } catch (error) {
        console.error("Error cancelling creative job after timeout", error);
        store.dispatch(
          LaunchError(error, false, [], {
            functionName: "waitForCreativeJobResponse",
            capture: "exception",
            context: { jobID, userID },
            tags: { source: "job" },
          })
        );
      }

      onReject(timeoutError);
    }, timeoutMsFormatted);

    unsubscribe = onValue(
      jobRef,
      (snapshot) => {
        const jobData = snapshot.val();

        if (!jobData) return;
        if (CREATIVE_JOB_IN_PROGRESS_STATUSES.has(jobData.status)) return;

        if (jobData.status === CREATIVE_JOB_STATUS.SUCCESS) {
          onResolve(jobData.response);
          return;
        }

        if (
          jobData.status === CREATIVE_JOB_STATUS.ERROR ||
          jobData.status === CREATIVE_JOB_STATUS.CANCELLED
        ) {
          const response = jobData.response || {};
          const isTimeout = response.code === JOB_TIMEOUT;
          onReject(
            getCreativeJobError({
              ...response,
              cancelled:
                isTimeout !== true &&
                (jobData.cancelled === true ||
                  jobData.status === CREATIVE_JOB_STATUS.CANCELLED),
            })
          );
          return;
        }
      },
      (error) => {
        onReject(error);
      }
    );
  });
};

export const startCancelCreativeJob =
  ({ jobID }) =>
  async (dispatch) => {
    if (!jobID) return { ok: false };

    try {
      const { userID } = dispatch(getPathDatabaseByRole());
      if (!userID) return { ok: false };

      await update(ref(db, `jobs/${userID}/${jobID}`), {
        status: CREATIVE_JOB_STATUS.CANCELLED,
        cancelled: true,
      });
      return { ok: true };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startCancelCreativeJob",
          capture: "exception",
          context: { jobID },
          tags: { source: "job" },
        })
      );
      return { ok: false };
    }
  };

export const createCreativeJobAndWaitForResponse = async ({
  dispatch,
  jobType,
  payload,
  timeoutMs,
  language: languageOverride,
  onJobCreated = () => {},
}) => {
  const { jobID, userID } = await createCreativeJob({
    dispatch,
    jobType,
    payload,
    language: languageOverride,
  });
  onJobCreated({ jobID });
  try {
    return await waitForCreativeJobResponse({
      userID,
      jobID,
      timeoutMs: getCreativeJobTimeoutMs({ jobType, timeoutMs }),
    });
  } catch (error) {
    if (isCreativeJobResultError(error)) {
      error.creativeJobID = jobID;
      error.creativeJobAdvertiserID = userID;
    }
    await dispatch(
      LaunchError(error, false, [], {
        functionName: "createCreativeJobAndWaitForResponse",
        creativeJobType: jobType,
        capture: "exception",
        context: { jobID, jobType },
        tags: { source: "job", action: "creativeJobs" },
      })
    );
    throw error;
  }
};
